import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type HealthcareSpecialty = 
  | 'oncologia'
  | 'dermatologia'
  | 'enfermagem'
  | 'geriatria'
  | 'estetica'
  | 'cuidados_paliativos'
  | 'fisioterapia'
  | 'medicina_geral'
  | 'outro';

type ProfessionalCouncil = 'CRM' | 'COREN' | 'CREFITO' | 'CRF' | 'CRN' | 'CRBM' | 'OUTRO';

export interface HealthcareProfessional {
  id: string;
  user_id: string;
  full_name: string;
  profession: string;
  specialty: HealthcareSpecialty;
  council_type: ProfessionalCouncil;
  council_number: string;
  council_state: string;
  professional_email: string;
  verified: boolean;
  created_at: string;
}

export interface ProfessionalRegistrationData {
  full_name: string;
  profession: string;
  specialty: HealthcareSpecialty;
  council_type: ProfessionalCouncil;
  council_number: string;
  council_state: string;
  professional_email: string;
}

interface HealthcareProfessionalContextType {
  professional: HealthcareProfessional | null;
  isProfessional: boolean;
  isLoading: boolean;
  isProfessionalModeActive: boolean;
  activateProfessionalMode: () => void;
  deactivateProfessionalMode: () => void;
  registerProfessional: (data: ProfessionalRegistrationData) => Promise<{ error: Error | null }>;
  refetch: () => Promise<void>;
}

const HealthcareProfessionalContext = createContext<HealthcareProfessionalContextType | undefined>(undefined);

const PROFESSIONAL_MODE_KEY = 'nathof_professional_mode_active';

export const HealthcareProfessionalProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [professional, setProfessional] = useState<HealthcareProfessional | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfessionalModeActive, setIsProfessionalModeActive] = useState(false);

  const fetchProfessional = async () => {
    if (!user) {
      setProfessional(null);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('healthcare_professionals')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching professional:', error);
        setProfessional(null);
      } else {
        setProfessional(data as HealthcareProfessional | null);
        // Auto-activate professional mode if they are a professional
        if (data) {
          const stored = localStorage.getItem(PROFESSIONAL_MODE_KEY);
          if (stored === 'true') {
            setIsProfessionalModeActive(true);
          }
        }
      }
    } catch (err) {
      console.error('Error:', err);
      setProfessional(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfessional();
  }, [user]);

  const activateProfessionalMode = () => {
    setIsProfessionalModeActive(true);
    localStorage.setItem(PROFESSIONAL_MODE_KEY, 'true');
  };

  const deactivateProfessionalMode = () => {
    setIsProfessionalModeActive(false);
    localStorage.removeItem(PROFESSIONAL_MODE_KEY);
  };

  const registerProfessional = async (data: ProfessionalRegistrationData) => {
    if (!user) {
      return { error: new Error('Usuário não autenticado') };
    }

    try {
      const { error } = await supabase
        .from('healthcare_professionals')
        .insert({
          user_id: user.id,
          full_name: data.full_name,
          profession: data.profession,
          specialty: data.specialty,
          council_type: data.council_type,
          council_number: data.council_number,
          council_state: data.council_state,
          professional_email: data.professional_email,
        });

      if (error) {
        return { error: new Error(error.message) };
      }

      await fetchProfessional();
      activateProfessionalMode();
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  return (
    <HealthcareProfessionalContext.Provider
      value={{
        professional,
        isProfessional: !!professional,
        isLoading,
        isProfessionalModeActive: !!professional && isProfessionalModeActive,
        activateProfessionalMode,
        deactivateProfessionalMode,
        registerProfessional,
        refetch: fetchProfessional,
      }}
    >
      {children}
    </HealthcareProfessionalContext.Provider>
  );
};

export const useHealthcareProfessional = () => {
  const context = useContext(HealthcareProfessionalContext);
  if (context === undefined) {
    throw new Error('useHealthcareProfessional must be used within a HealthcareProfessionalProvider');
  }
  return context;
};
